import { batchActions } from 'util/redux'

import { doOpenSearchPage } from 'actions/pages/doOpenSearchPage'

import * as types from 'constants/action_types'
import { DEFAULT_SORT_ORDER } from 'constants/defaults'

import { fetchingStatusesSelector } from 'selectors/app'

import { selectCurrentMailbox } from 'selectors/mailboxes/selectCurrentMailbox'
import { selectIsMerging } from 'selectors/page/selectIsMerging'
import {
  selectCurrentMergeSearch,
  selectCurrentMergeSearchQueryID,
  selectCurrentMergeSearchQueryObject,
  selectCurrentMergeSearchQueryObjectForAPI,
  selectCurrentTicketSearchQueryId,
  selectDefaultTicketSearch,
  selectDefaultTicketSearchId,
  selectTicketSearchOperatorValueMap,
  shouldFetchMergeSearchByQueryString,
  shouldFetchIncompleteSearch,
  selectCurrentCommittedTicketSearchQueryString,
  selectIsFreeFormSearch,
} from 'selectors/search'

import {
  constructGraphQLSearchQueryObject,
  storeSubmittedSearchMailboxAndQueryString,
  getSubmittedSearchMailboxAndQueryString,
} from 'util/search'
import { recentSearchQueries } from 'components/ConversationList/Header/Search/util'
import {
  selectCurrentSubmittedTicketSearchQueryObject,
  selectSearchMailbox,
} from 'selectors/search/searchFilters'
import { selectMailboxes } from 'selectors/mailboxes/selectMailboxes'
import { selectIsOnSearchPage } from 'selectors/location'
import { doLoadSearchTickets } from './doLoadSearchTickets'
import { doFetchTicketSearchByQueryString } from './doFetchTicketSearchByQueryString'
import { doFetchTicketSearch } from './doFetchTicketSearch'

export function doClearNonNamedTicketSearchResults({
  keepCurrent = false,
  refetchListOnNextUpdate = false,
} = {}) {
  return (dispatch, getState) => {
    // Elasticsearch refresh interval is set to one second, which means it is
    // possible for ES to index a change but for it to not be available within 1000ms
    // If we query for updated results instantly we might hit old indexed data, which
    // are incorrect. Which is why we defer clearing of old results by 1 second,
    // guaranteeing that we will not hit previous results
    const state = getState()
    setTimeout(() => {
      dispatch({
        type: types.SEARCH_CLEAR_TICKET_RESULTS,
        data: {
          omitNamedSearches: true,
          valueIdMap: selectTicketSearchOperatorValueMap(state),
          queryIdToKeep: keepCurrent
            ? selectCurrentTicketSearchQueryId(state)
            : undefined,
          refetchListOnNextUpdate,
        },
      })
      // GR: Since we no longer use triggers, we have to manually refresh the
      // ticket list search results
      dispatch(doLoadSearchTickets())
    }, 1000)
  }
}

export function doClearTicketSearchResults(options = {}) {
  return {
    type: types.SEARCH_CLEAR_TICKET_RESULTS,
    data: options,
  }
}

export function doUpdateLatestSearch() {
  return (dispatch, getState) => {
    const queryId = selectCurrentTicketSearchQueryId(getState())
    const isMerging = selectIsMerging(getState())
    return dispatch({
      type: types.STORE_LATEST_SEARCH,
      data: { queryId, isMerging },
    })
  }
}

export const doFetchDefaultTicketSearch = () => {
  return (dispatch, getState) => {
    const state = getState()
    const query = selectDefaultTicketSearch(state)
    const queryId = selectDefaultTicketSearchId(state)
    const mailbox = selectCurrentMailbox(state)
    const valueMap = selectTicketSearchOperatorValueMap(state)
    const apiQuery = constructGraphQLSearchQueryObject({ ...query }, valueMap)

    // we need to dispatch SET_CURRENT_TICKET_LIST, because we want to make sure
    // that the default set is set at the currentTicketList. We do it here
    // because we cannot differentiate between a regular search and a default
    // search in the reducer
    dispatch(
      batchActions(
        doFetchTicketSearch(
          queryId,
          query,
          apiQuery,
          undefined,
          undefined,
          undefined,
          { storeLastSearchQueryId: false }
        ),
        {
          type: types.SET_CURRENT_TICKET_LIST,
          data: { query: queryId, mailbox, sortOrder: DEFAULT_SORT_ORDER },
        }
      )
    )
  }
}

// Checks if tickets cached, otherwise fetches from API
export function doLoadMergeSearchByQueryString(page = 1) {
  return (dispatch, getState) => {
    if (!shouldFetchMergeSearchByQueryString(getState())) return false
    return dispatch(doFetchMergeSearchByQueryString(page))
  }
}

export function doFetchMergeSearchByQueryString(page = 1) {
  return (dispatch, getState) => {
    const state = getState()
    const statuses = fetchingStatusesSelector(state)
    if (statuses.fetchTicketSearch) return
    const searchQueryId = selectCurrentMergeSearchQueryID(state)
    const searchQueryObject = selectCurrentMergeSearchQueryObject(state)
    const sortOrder = DEFAULT_SORT_ORDER
    const searchQueryObjectForAPI = selectCurrentMergeSearchQueryObjectForAPI(
      state
    )
    const previousPages = selectCurrentMergeSearch(state)
    const previous = previousPages && previousPages[page]
    // eslint-disable-next-line consistent-return
    if (previous && previous.length > 1) return Promise.resolve() // no need to dispatch

    dispatch({
      type: types.MERGE_SEARCH_REQUEST,
      data: {
        searchQueryId,
        searchQueryObject,
        searchQueryObjectForAPI,
        sortOrder,
        page,
      },
    })

    // eslint-disable-next-line consistent-return
    return dispatch(
      doFetchTicketSearch(
        searchQueryId,
        searchQueryObject,
        searchQueryObjectForAPI,
        page
      )
    ).then(({ currentPage, totalPages, totalCount, tickets }) => {
      dispatch({
        type: types.MERGE_SEARCH_SUCCESS,
        data: {
          searchQueryId,
          searchQueryObject,
          searchQueryObjectForAPI,
          page,
          currentPage,
          totalPages,
          totalCount,
          tickets,
        },
      })
      return { currentPage, totalPages, totalCount, tickets }
    })
  }
}

export function doUpdateTicketSearchQuery(queryString, options) {
  const {
    commit = true,
    position,
    reset = false,
    currentPart,
    submit = true,
    shouldIncludeMailboxSearch,
  } = options
  return (dispatch, getState) => {
    const state = getState()
    const trimmedQueryString = queryString && queryString.trim()
    const searchMailbox = selectSearchMailbox(state)
    const isTypedSearch = true
    const submitData = submit
      ? getSubmittedSearchMailboxAndQueryString({
          searchMailbox,
          queryString: trimmedQueryString,
          mailboxes: selectMailboxes(state),
          shouldIncludeMailboxSearch,
        })
      : null

    dispatch({
      type: types.UPDATE_CURRENT_TICKET_SEARCH_QUERY,
      data: {
        commit,
        position,
        queryString: submit ? submitData?.queryString : queryString,
        currentPart,
        submit,
        reset,
        isTypedSearch,
        searchMailbox: submit ? submitData?.searchMailbox : searchMailbox,
      },
    })

    if (!submit) {
      return
    }
    if (submitData.fullQueryString) {
      recentSearchQueries.value = submitData.fullQueryString
      // we need to store the submitted search query string in session storage to persist it on refresh
      storeSubmittedSearchMailboxAndQueryString(submitData)
      dispatch(doOpenSearchPage(submitData.fullQueryString, isTypedSearch))
    } else {
      const isOnSearchPage = selectIsOnSearchPage(state)
      if (isOnSearchPage) {
        dispatch(doOpenSearchPage(selectDefaultTicketSearchId(state)))
      }
    }
  }
}

export function doToggleListSearchBoxFocused(isFocused) {
  return {
    type: types.TOGGLE_LIST_SEARCH_BOX_STATUS,
    data: { isFocused },
  }
}

export function doMarkSearchAsComplete(queryId) {
  return {
    type: types.MARK_SEARCH_AS_COMPLETE,
    data: { queryId },
  }
}

// This is a version of doFetchTicketSearch which is used
// when you expect a bunch of changes to be happening in short succession
// it is both a performance improvement and race condition protection
let debounceTimeout
export function doLoadSearchTicketsDebounced() {
  clearTimeout(debounceTimeout)
  return (dispatch, getState) => {
    const state = getState()
    const queryId = selectCurrentTicketSearchQueryId(state)

    const callback = () => dispatch(doFetchTicketSearchByQueryString())
    debounceTimeout = setTimeout(callback, 3000)
    dispatch(doMarkSearchAsComplete(queryId))
  }
}

let debounceLoad
export function doLoadIncompleteSearch() {
  clearTimeout(debounceLoad)
  return (dispatch, getState) => {
    const state = getState()
    const should = shouldFetchIncompleteSearch(state)
    if (should) {
      const queryId = selectCurrentTicketSearchQueryId(state)
      dispatch(doMarkSearchAsComplete(queryId))
      debounceLoad = setTimeout(() => {
        dispatch(doFetchTicketSearchByQueryString())
      }, 5000)
    }
  }
}

export const doSubmitTicketsSearch = (
  query,
  { shouldIncludeMailboxSearch } = {}
) => {
  return (dispatch, getState) => {
    const state = getState()
    const committedSearchQuery = selectCurrentCommittedTicketSearchQueryString(
      state
    )
    const searchQuery = query || committedSearchQuery
    const isFreeFormSearch = selectIsFreeFormSearch(state)
    dispatch(
      doUpdateTicketSearchQuery(searchQuery, {
        commit: true,
        reset: !isFreeFormSearch,
        submit: true,
        shouldIncludeMailboxSearch,
      })
    )
    return selectCurrentSubmittedTicketSearchQueryObject(getState())
  }
}

export const doUpdateSearchMailbox = mailbox => {
  return {
    type: types.SEARCH_MAILBOX_UPDATE,
    payload: {
      mailbox:
        mailbox?.id === 'all' || !mailbox
          ? null
          : { id: mailbox.id, name: mailbox.name },
    },
  }
}

export const doUpdateSearchByKey = (key, value) => {
  return {
    type: types.SEARCH_UPDATE_BY_KEY,
    payload: { key, value },
  }
}
